/**
 * CopilotPanel — Persistent AI Co-pilot panel for forensic analysis.
 *
 * A persistent sliding/docked panel component. Collapsible, not a modal.
 * Mounts in accountant work area layout and War Room layout.
 *
 * Role-lensed:
 * - accountants: whatif + diff UI prominently
 * - war-room: scenario envelope + accuracy metrics prominently
 *
 * Design constraints:
 * - Colors: ONLY var(--color-brand) for signal, var(--color-done) for done
 * - Type: DM Serif (headings), Outfit (body), DM Mono (numbers)
 * - Dark default; respects data-theme for Daylight
 */

import React, { useState, useEffect } from 'react';
import { PanelRight, PanelRightClose, Sparkles, BarChart3, GitCompare, Target, Activity } from 'lucide-react';
import { cn } from '../../lib/utils';
import { BRAND_NAME } from '../../config/brand';
import { useCopilot } from '../../hooks/useCopilot';
import { WhatIfForm } from './WhatIfForm';
import { EngineRunDiff } from './EngineRunDiff';
import { ScenarioEnvelope } from './ScenarioEnvelope';
import { AccuracyMetrics } from './AccuracyMetrics';

type CopilotTab = 'whatif' | 'diff' | 'scenarios' | 'accuracy';
type CopilotRole = 'accountant' | 'war-room' | 'analyst' | 'attorney';

interface CopilotPanelProps {
  /** Case/report ID for context */
  reportId: string;
  /** Role lens determines which tabs are prominent */
  role?: CopilotRole;
  /** Initial active tab */
  defaultTab?: CopilotTab;
  /** Initially expanded state */
  defaultExpanded?: boolean;
  /** Callback when panel is toggled */
  onExpandedChange?: (expanded: boolean) => void;
  /** Additional classes */
  className?: string;
}

const tabConfig: Record<CopilotTab, { label: string; icon: React.ReactNode; description: string }> = {
  whatif: {
    label: 'What-If',
    icon: <Sparkles className="w-4 h-4" />,
    description: 'Run exploratory scenarios',
  },
  diff: {
    label: 'Compare Runs',
    icon: <GitCompare className="w-4 h-4" />,
    description: 'See what changed between runs',
  },
  scenarios: {
    label: 'Scenarios',
    icon: <Target className="w-4 h-4" />,
    description: 'Pre-war-room scenario envelope',
  },
  accuracy: {
    label: 'Accuracy',
    icon: <Activity className="w-4 h-4" />,
    description: 'Engine prediction accuracy metrics',
  },
};

/**
 * Determine tab priority based on role
 */
function getTabsForRole(role: CopilotRole): CopilotTab[] {
  switch (role) {
    case 'accountant':
      return ['whatif', 'diff', 'scenarios', 'accuracy'];
    case 'war-room':
      return ['scenarios', 'accuracy', 'whatif', 'diff'];
    case 'analyst':
      return ['whatif', 'scenarios', 'accuracy', 'diff'];
    case 'attorney':
      return ['scenarios', 'accuracy', 'whatif'];
    default:
      return ['whatif', 'diff', 'scenarios', 'accuracy'];
  }
}

export function CopilotPanel({
  reportId,
  role = 'accountant',
  defaultTab,
  defaultExpanded = false,
  onExpandedChange,
  className,
}: CopilotPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState<CopilotTab>(
    defaultTab || getTabsForRole(role)[0]
  );

  const {
    isLoading,
    error,
    clearError,
    whatIfResult,
    engineDiffResult,
    scenariosResult,
    accuracyResult,
    runEngineWhatIf,
    getEngineRunDiff,
    getPrewarroomScenarios,
    getEngineAccuracy,
  } = useCopilot();

  // Load initial data based on active tab
  useEffect(() => {
    if (!expanded) return;

    clearError();

    switch (activeTab) {
      case 'scenarios':
        getPrewarroomScenarios(reportId);
        break;
      case 'accuracy':
        getEngineAccuracy();
        break;
      default:
        // whatif and diff are loaded on-demand
        break;
    }
  }, [expanded, activeTab, reportId, clearError, getPrewarroomScenarios, getEngineAccuracy]);

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  const tabs = getTabsForRole(role);

  return (
    <div
      className={cn(
        'flex flex-col transition-all duration-300 ease-in-out border-l',
        expanded ? 'w-[400px]' : 'w-[48px]',
        'bg-[var(--color-bg-surface)] border-[var(--color-border)]',
        className
      )}
      data-testid="copilot-panel"
    >
      {/* Header / Toggle */}
      <div
        className={cn(
          'flex items-center border-b border-[var(--color-border)]',
          expanded ? 'justify-between px-4 py-3' : 'justify-center py-3'
        )}
      >
        {expanded ? (
          <>
            <div className="flex items-center gap-2">
              <Sparkles
                className="w-5 h-5"
                style={{ color: 'var(--color-brand)' }}
                data-testid="copilot-icon"
              />
              <span className="font-serif text-lg font-semibold text-[var(--color-text-primary)]">
                {BRAND_NAME} Copilot
              </span>
            </div>
            <button
              onClick={handleToggle}
              className="p-1.5 rounded-md hover:bg-[var(--color-bg-hover)] transition-colors"
              aria-label="Collapse panel"
              data-testid="copilot-collapse"
            >
              <PanelRightClose className="w-5 h-5 text-[var(--color-text-muted)]" />
            </button>
          </>
        ) : (
          <button
            onClick={handleToggle}
            className="p-1.5 rounded-md hover:bg-[var(--color-bg-hover)] transition-colors"
            aria-label="Expand panel"
            data-testid="copilot-expand"
          >
            <PanelRight
              className="w-6 h-6"
              style={{ color: 'var(--color-brand)' }}
            />
          </button>
        )}
      </div>

      {/* Content */}
      {expanded && (
        <>
          {/* Tab Navigation */}
          <div className="flex border-b border-[var(--color-border)] overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors',
                  'border-b-2 -mb-px',
                  activeTab === tab
                    ? 'border-[var(--color-brand)] text-[var(--color-brand)]'
                    : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
                )}
                data-testid={`copilot-tab-${tab}`}
              >
                {tabConfig[tab].icon}
                <span>{tabConfig[tab].label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Error Display */}
            {error && (
              <div
                className="mb-4 p-3 rounded-md text-sm"
                style={{
                  backgroundColor: 'rgba(255, 102, 0, 0.12)',
                  border: '1px solid rgba(255, 102, 0, 0.30)',
                  color: 'var(--color-brand)',
                }}
                data-testid="copilot-error"
              >
                <div className="font-medium mb-1">Error</div>
                <div className="opacity-90">{error.message}</div>
                <button
                  onClick={clearError}
                  className="mt-2 text-xs underline hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Loading State */}
            {isLoading && !whatIfResult && !engineDiffResult && !scenariosResult && !accuracyResult && (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                    style={{ borderColor: 'var(--color-brand)', borderTopColor: 'transparent' }}
                  />
                  <span className="text-sm text-[var(--color-text-muted)]">
                    Loading...
                  </span>
                </div>
              </div>
            )}

            {/* Tab Panels */}
            {activeTab === 'whatif' && (
              <WhatIfForm
                reportId={reportId}
                onRun={runEngineWhatIf}
                result={whatIfResult}
                isLoading={isLoading}
              />
            )}

            {activeTab === 'diff' && (
              <EngineRunDiff
                reportId={reportId}
                onCompare={getEngineRunDiff}
                result={engineDiffResult}
                isLoading={isLoading}
              />
            )}

            {activeTab === 'scenarios' && (
              <ScenarioEnvelope
                result={scenariosResult}
                isLoading={isLoading}
              />
            )}

            {activeTab === 'accuracy' && (
              <AccuracyMetrics
                result={accuracyResult}
                isLoading={isLoading}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default CopilotPanel;
